import { SubSelectCard } from '../../components/SubSelectCard';
import { FloatingSelectField } from '../../components/FloatingSelectField';
import { InfoNote } from '../../components/InfoNote';
import { SectionHeader } from '../../components/SectionHeader';
import { SkeletonBlock } from '../../components/SkeletonBlock';
import { TimeChangeIcon } from '../../icons/TimeChangeIcon';
import { ActionBar } from '../ActionBar';
import { PanelActionFooter } from '../PanelActionFooter';
import { PreferencesPanel } from '../PreferencesPanel';
import {
  DateSection,
  EmptyTimeslots,
  FormGroup,
  Heading,
  HeadingAccent,
  Inner,
  OutlinedCard,
  PreferencesCardBody,
  ScrollArea,
  Wrapper,
} from './PackageDiningContentPanel.styles';
import type { PackageDiningContentPanelProps } from './PackageDiningContentPanel.types';
import { radius } from '../../theme/tokens';

export function PackageDiningContentPanel({
  dateGroups,
  onDayChange,
  onRestaurantChange,
  onMenuChange,
  onTimeslotChange,
  preferences = [],
  onPreferenceOptionToggle,
  onPreferenceOtherChange,
  onPreferenceFreeTextChange,
  onPreferenceExpandedChange,
  onBack,
  onItineraryClick,
  currentStep = 1,
  totalSteps,
  footerTitle,
  totalAmount,
  currencyCode,
  locale = 'en-GB',
  onNext,
  nextDisabled = false,
  isLoading = false,
  labels = {},
}: PackageDiningContentPanelProps) {
  const {
    title = 'Reserve a table.',
    titleAccent = "Pick a time, we'll save you a seat.",
    dateSubtitle = 'When would you like to eat?',
    backAriaLabel = 'Go back',
    progressLabel = 'Booking progress',
    progressValueText,
    itineraryLabel,
    dayLabel = 'Day',
    restaurantLabel = 'Restaurant',
    menuLabel = 'Menu',
    timeLabel = 'Time',
    noTimeslots = 'No times available',
    otherButtonLabel,
    totalLabel = 'Total',
    nextLabel = 'Next',
  } = labels;

  return (
    <Wrapper aria-busy={isLoading || undefined}>
      <ScrollArea>
        <Inner>
          <ActionBar
            onBack={onBack}
            backAriaLabel={backAriaLabel}
            currentStep={currentStep}
            totalSteps={totalSteps}
            progressLabel={progressLabel}
            progressValueText={progressValueText}
            onItineraryClick={onItineraryClick}
            itineraryLabel={itineraryLabel}
          />
          {isLoading ? (
            <>
              <Heading>
                <SkeletonBlock variant="heading" />
              </Heading>
              <FormGroup>
                <SkeletonBlock height="220px" borderRadius={radius('card', 'radius-xl')} />
                <SkeletonBlock height="220px" borderRadius={radius('card', 'radius-xl')} />
              </FormGroup>
            </>
          ) : (
            <>
              <Heading>
                {title} <HeadingAccent>{titleAccent}</HeadingAccent>
              </Heading>
              <FormGroup>
                <OutlinedCard>
                  {dateGroups.map((group) => (
                    <DateSection key={group.id}>
                      <SectionHeader title={group.date} subtitle={dateSubtitle} />
                      {group.options.map((option) => {
                        const hasRestaurantChoice = (option.restaurantOptions?.length ?? 0) > 1;
                        const isWaitingForRestaurant =
                          hasRestaurantChoice && !option.selectedRestaurant;

                        return (
                          <SubSelectCard key={option.id} heading={option.title}>
                            {(option.dayOptions?.length ?? 0) > 1 && (
                              <FloatingSelectField
                                label={dayLabel}
                                options={option.dayOptions ?? []}
                                value={option.selectedDay}
                                onChange={(value) => onDayChange?.(option.id, value)}
                                aria-label={`${dayLabel} ${option.title}`}
                              />
                            )}
                            {hasRestaurantChoice && (
                              <FloatingSelectField
                                label={restaurantLabel}
                                options={option.restaurantOptions ?? []}
                                value={option.selectedRestaurant}
                                required
                                onChange={(value) => onRestaurantChange?.(option.id, value)}
                                aria-label={`${restaurantLabel} ${option.title}`}
                              />
                            )}
                            {!isWaitingForRestaurant && (
                              <>
                                {option.menuOptions.length > 0 && (
                                  <FloatingSelectField
                                    label={menuLabel}
                                    options={option.menuOptions}
                                    value={option.selectedMenu}
                                    required={Boolean(option.menuRequired)}
                                    onChange={(value) => onMenuChange(option.id, value)}
                                    aria-label={`${menuLabel} ${option.title}`}
                                  />
                                )}
                                {option.isLoading ? (
                                  <SkeletonBlock
                                    height="72px"
                                    borderRadius={radius('card', 'radius-md')}
                                  />
                                ) : option.timeslotOptions.length ? (
                                  <FloatingSelectField
                                    label={timeLabel}
                                    options={option.timeslotOptions}
                                    value={option.selectedTimeslot}
                                    onChange={(value) => onTimeslotChange(option.id, value)}
                                    aria-label={`${timeLabel} ${option.title}`}
                                  />
                                ) : (
                                  <EmptyTimeslots>{noTimeslots}</EmptyTimeslots>
                                )}
                                {option.noteText && (
                                  <InfoNote
                                    text={option.noteText}
                                    icon={<TimeChangeIcon size={18} />}
                                  />
                                )}
                              </>
                            )}
                          </SubSelectCard>
                        );
                      })}
                    </DateSection>
                  ))}
                </OutlinedCard>
                {preferences.length > 0 && (
                  <OutlinedCard>
                    <PreferencesCardBody>
                      <PreferencesPanel
                        categories={preferences}
                        onOptionToggle={(categoryId, option) =>
                          onPreferenceOptionToggle?.(categoryId, option)
                        }
                        onOtherChange={(categoryId, value) =>
                          onPreferenceOtherChange?.(categoryId, value)
                        }
                        onFreeTextChange={(categoryId, value) =>
                          onPreferenceFreeTextChange?.(categoryId, value)
                        }
                        onExpandedChange={onPreferenceExpandedChange}
                        otherButtonLabel={otherButtonLabel}
                      />
                    </PreferencesCardBody>
                  </OutlinedCard>
                )}
              </FormGroup>
            </>
          )}
        </Inner>
      </ScrollArea>
      <PanelActionFooter
        title={footerTitle}
        totalLabel={totalLabel}
        totalAmount={totalAmount}
        currencyCode={currencyCode}
        locale={locale}
        ctaLabel={nextLabel}
        onCtaClick={onNext}
        ctaDisabled={nextDisabled}
        isLoading={isLoading}
      />
    </Wrapper>
  );
}
